import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { VoucherDto, ClaimVouchersDto, RevokeVouchersDto, PaginatedResponse, VoucherInsightsDto, PartnerZoneDto, PendingRedemptionCustomerDto, SaveFollowUpDto, PendingRedemptionFilterParams } from '../models/voucher.model';
import { VoucherFilterParams } from '../models/voucher-filter.model';

@Injectable({
    providedIn: 'root'
})
export class VoucherService {
    private apiUrl = `${environment.apiConfig.uri}/api/Voucher`;

    constructor(private http: HttpClient) { }

    /**
     * Get vouchers for a specific customer
     * @param customerId - The customer ID
     * @returns Observable of voucher array
     */
    getCustomerVouchers(customerId: string): Observable<VoucherDto[]> {
        return this.http.get<VoucherDto[]>(`${this.apiUrl}/customer/${customerId}`);
    }

    /**
     * Get vouchers for the authenticated partner with optional filters
     * @param filters - Optional filter parameters
     * @returns Observable of paginated voucher response
     */
    getPartnerVouchers(filters?: VoucherFilterParams): Observable<PaginatedResponse<VoucherDto>> {
        let params = new HttpParams();

        if (filters) {
            if (filters.phoneNumber) params = params.set('PhoneNumber', filters.phoneNumber);
            if (filters.customerName) params = params.set('CustomerName', filters.customerName);
            if (filters.partnerName) params = params.set('PartnerName', filters.partnerName);
            if (filters.minVoucherAmount !== undefined) params = params.set('MinVoucherAmount', filters.minVoucherAmount.toString());
            if (filters.maxVoucherAmount !== undefined) params = params.set('MaxVoucherAmount', filters.maxVoucherAmount.toString());
            if (filters.partnerId) params = params.set('PartnerId', filters.partnerId);
            if (filters.outletId) params = params.set('OutletId', filters.outletId);
            if (filters.status !== undefined) params = params.set('Status', filters.status.toString());
            if (filters.redemptionReference) params = params.set('RedemptionReference', filters.redemptionReference);
            if (filters.fromDate) params = params.set('FromDate', filters.fromDate);
            if (filters.toDate) params = params.set('ToDate', filters.toDate);
            if (filters.pageIndex !== undefined) params = params.set('PageIndex', filters.pageIndex.toString());
            if (filters.pageSize !== undefined) params = params.set('PageSize', filters.pageSize.toString());
        }

        return this.http.get<PaginatedResponse<VoucherDto>>(`${this.apiUrl}/partner`, { params });
    }

    /**
     * Get vouchers for the authenticated outlet user with optional filters
     * @param filters - Optional filter parameters
     * @returns Observable of paginated voucher response
     */
    getOutletVouchers(filters?: VoucherFilterParams): Observable<PaginatedResponse<VoucherDto>> {
        let params = new HttpParams();

        if (filters) {
            if (filters.phoneNumber) params = params.set('PhoneNumber', filters.phoneNumber);
            if (filters.customerName) params = params.set('CustomerName', filters.customerName);
            if (filters.partnerName) params = params.set('PartnerName', filters.partnerName);
            if (filters.minVoucherAmount !== undefined) params = params.set('MinVoucherAmount', filters.minVoucherAmount.toString());
            if (filters.maxVoucherAmount !== undefined) params = params.set('MaxVoucherAmount', filters.maxVoucherAmount.toString());
            if (filters.partnerId) params = params.set('PartnerId', filters.partnerId);
            if (filters.outletId) params = params.set('OutletId', filters.outletId);
            if (filters.status !== undefined) params = params.set('Status', filters.status.toString());
            if (filters.redemptionReference) params = params.set('RedemptionReference', filters.redemptionReference);
            if (filters.fromDate) params = params.set('FromDate', filters.fromDate);
            if (filters.toDate) params = params.set('ToDate', filters.toDate);
            if (filters.pageIndex !== undefined) params = params.set('PageIndex', filters.pageIndex.toString());
            if (filters.pageSize !== undefined) params = params.set('PageSize', filters.pageSize.toString());
        }

        return this.http.get<PaginatedResponse<VoucherDto>>(`${this.apiUrl}/outlet`, { params });
    }

    /**
     * Claim vouchers
     * @param claimRequest - The claim vouchers request
     * @returns Observable of claimed voucher array
     */
    claimVouchers(claimRequest: ClaimVouchersDto): Observable<VoucherDto[]> {
        return this.http.post<VoucherDto[]>(`${this.apiUrl}/claim`, claimRequest);
    }

    /**
     * Revoke vouchers
     * @param revokeRequest - The revoke vouchers request
     * @returns Observable of revoked voucher array
     */
    revokeVouchers(revokeRequest: RevokeVouchersDto): Observable<VoucherDto[]> {
        return this.http.post<VoucherDto[]>(`${this.apiUrl}/revoke`, revokeRequest);
    }

    /**
     * Get voucher insights for partner
     * @param fromDate - Optional start date for filtering
     * @param toDate - Optional end date for filtering
     * @returns Observable of VoucherInsightsDto
     */
    getPartnerInsights(fromDate?: string, toDate?: string): Observable<VoucherInsightsDto> {
        let params = new HttpParams();

        if (fromDate) params = params.set('FromDate', fromDate);
        if (toDate) params = params.set('ToDate', toDate);

        return this.http.get<VoucherInsightsDto>(`${environment.apiConfig.uri}/api/VoucherInsights/partner`, { params });
    }

    /**
     * Get voucher insights for outlet
     * @param fromDate - Optional start date for filtering
     * @param toDate - Optional end date for filtering
     * @returns Observable of VoucherInsightsDto
     */
    getOutletInsights(fromDate?: string, toDate?: string): Observable<VoucherInsightsDto> {
        let params = new HttpParams();

        if (fromDate) params = params.set('FromDate', fromDate);
        if (toDate) params = params.set('ToDate', toDate);

        return this.http.get<VoucherInsightsDto>(`${environment.apiConfig.uri}/api/VoucherInsights/outlet`, { params });
    }

    /**
     * Get customers with pending redemptions
     * @param filters - Filter and sort criteria
     * @returns Observable of paginated pending redemption customer response
     */
    getPendingRedemptionCustomers(filters?: PendingRedemptionFilterParams): Observable<PaginatedResponse<PendingRedemptionCustomerDto>> {
        let params = new HttpParams();

        if (filters) {
            if (filters.customerName) params = params.set('CustomerName', filters.customerName);
            if (filters.customerPhoneNumber) params = params.set('CustomerPhoneNumber', filters.customerPhoneNumber);
            if (filters.zoneIds) params = params.set('ZoneIds', filters.zoneIds);
            if (filters.status) params = params.set('Status', filters.status);
            if (filters.sortBy) params = params.set('SortBy', filters.sortBy);
            if (filters.sortDescending !== undefined) params = params.set('SortDescending', filters.sortDescending.toString());
            if (filters.pageIndex !== undefined) params = params.set('PageIndex', filters.pageIndex.toString());
            if (filters.pageSize !== undefined) params = params.set('PageSize', filters.pageSize.toString());
        }

        return this.http.get<PaginatedResponse<PendingRedemptionCustomerDto>>(`${this.apiUrl}/partner/pending-redemption-customers`, { params });
    }

    /**
     * Get unique zones for the logged-in partner's configurations
     * @returns Observable of PartnerZoneDto array
     */
    getPartnerZones(): Observable<PartnerZoneDto[]> {
        return this.http.get<PartnerZoneDto[]>(`${this.apiUrl}/partner/zones`);
    }

    /**
     * Save customer follow-up
     * @param dto - Save follow-up payload
     * @returns Observable of saved follow-up entity
     */
    saveFollowUp(dto: SaveFollowUpDto): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/partner/follow-up`, dto);
    }
}
